import React, { useMemo, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import './TrainingReportGenerate.css';

const formatGeneratedAt = (value) => {
  if (!value) return 'Pendiente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX');
};

export const TrainingReportGenerateStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
  onBackToList,
}) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState(null);

  const generated = report?.estado === 'generado';
  const fileName = report?.nombre_archivo || `reporte_capacitacion_${idReporte}.pdf`;
  const generatedAt = useMemo(
    () => formatGeneratedAt(report?.generado_en),
    [report?.generado_en],
  );

  const generateFinal = async () => {
    if (!accessToken || generating || downloading) return;

    const confirmed = window.confirm(
      generated
        ? 'Se generará nuevamente el PDF definitivo y se reemplazará la versión registrada. ¿Deseas continuar?'
        : 'Se generará el PDF definitivo y el reporte cambiará a estado GENERADO. ¿Deseas continuar?',
    );
    if (!confirmed) return;

    setGenerating(true);
    setStatus({
      type: 'info',
      message: generated
        ? 'Regenerando PDF definitivo...'
        : 'Generando PDF definitivo...',
    });

    try {
      const updated = await trainingReportApi.generate(idReporte, accessToken);
      setReport(updated);
      setStatus({
        type: 'success',
        message: generated
          ? 'PDF definitivo regenerado correctamente.'
          : 'Reporte generado correctamente. Ya puedes descargar el PDF definitivo.',
      });
    } catch (error) {
      console.error('No se pudo generar el reporte definitivo:', error);
      setStatus({
        type: 'error',
        message:
          error?.message || 'No fue posible generar el PDF definitivo.',
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadFinal = async () => {
    if (!accessToken || !generated || generating || downloading) return;

    setDownloading(true);
    setStatus({ type: 'info', message: 'Preparando descarga...' });

    try {
      const blob = await trainingReportApi.download(idReporte, accessToken);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setStatus({
        type: 'success',
        message: 'Descarga iniciada correctamente.',
      });
    } catch (error) {
      console.error('No se pudo descargar el reporte:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible descargar el PDF definitivo.',
      });
    } finally {
      setDownloading(false);
    }
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 7 de 7</p>
          <h2>Generar reporte definitivo</h2>
          <p>
            Genera y registra el PDF definitivo. Después podrás descargar la versión
            almacenada por el sistema.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {generated ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div
        className={`training-report-generate-hero ${
          generated
            ? 'training-report-generate-hero-generated'
            : 'training-report-generate-hero-ready'
        }`}
      >
        <div>
          <span>{generated ? 'Documento definitivo' : 'Listo para generar'}</span>
          <strong>
            {generated
              ? 'El reporte ya cuenta con un PDF definitivo'
              : 'La vista previa fue revisada'}
          </strong>
          <small>
            {generated
              ? 'Puedes descargarlo o regenerarlo si necesitas reemplazar la versión registrada.'
              : 'La generación vuelve a validar todos los requisitos en backend antes de guardar el archivo.'}
          </small>
        </div>

        <button
          className="admin-operation-button"
          type="button"
          onClick={generateFinal}
          disabled={generating || downloading}
        >
          {generating
            ? 'Generando...'
            : generated
              ? 'Regenerar PDF definitivo'
              : 'Generar PDF definitivo'}
        </button>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      {generated && (
        <>
          <div className="training-report-generate-details">
            <div className="training-report-generate-detail">
              <span>Archivo</span>
              <strong>{fileName}</strong>
            </div>
            <div className="training-report-generate-detail">
              <span>Generado</span>
              <strong>{generatedAt}</strong>
            </div>
            <div className="training-report-generate-detail training-report-field-wide">
              <span>SHA-256</span>
              <code>{report?.hash_pdf || 'No disponible'}</code>
            </div>
          </div>

          <div className="admin-operation-status admin-operation-status-success">
            El estado del reporte es GENERADO. El archivo que descargues corresponde a
            la versión definitiva almacenada por el backend.
          </div>
        </>
      )}

      <div className="admin-operation-status admin-operation-status-warning training-report-generate-warning">
        Si después modificas datos generales, participantes, asistencias, resultados o
        evidencias, el reporte volverá a BORRADOR y tendrás que revisar y generar una
        nueva versión definitiva.
      </div>

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={generating || downloading}
        >
          Volver a vista previa
        </button>
        {generated && (
          <button
            className="admin-operation-button training-report-generate-download"
            type="button"
            onClick={downloadFinal}
            disabled={generating || downloading}
          >
            {downloading ? 'Descargando...' : 'Descargar PDF definitivo'}
          </button>
        )}
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onBackToList}
          disabled={generating || downloading}
        >
          Volver al listado
        </button>
      </div>
    </>
  );
};
